"use client"

import React, { useState } from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import Lightbox from './Lightbox';
import { colorForLink } from '@/lib/navColor';
import { RELAUNCH_HERO_HEIGHT_CLASS, RELAUNCH_CTA_CLASS } from '@/lib/relaunchLayout';

type ImageColor = { textColor: string; textShadow: string; bgColor: string; isDark: boolean };
type ImageGalleryProps = {
  images: { src: string; alt?: string; blurDataURL?: string; color?: ImageColor }[];
  title?: string;
  subtitle?: string;
  ctaLink?: string;
  ctaLabel?: string;
  autocycle?: number; // interval in seconds
  variant?: 'slider' | 'grid';
  displayVariant?: 'fullscreen' | 'inline'; // For backward compatibility
  // "playful" (default): full-viewport hero, palette-pill CTA. "relaunch":
  // "Layout-Startseite" spec - a fixed 70vh hero, flat green small-radius CTA.
  heroStyle?: 'playful' | 'relaunch';
};

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  title,
  subtitle,
  ctaLink,
  ctaLabel,
  autocycle,
  variant = 'slider',
  displayVariant = 'fullscreen', // Default to inline for content usage
  heroStyle = 'playful',
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isFullscreen = displayVariant === 'fullscreen';
  const isRelaunch = heroStyle === 'relaunch';

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Grid variant component - Modern masonry-style layout
  const GridGallery = () => (
    <div className={`image-gallery image-gallery-grid relative ${isFullscreen ? 'min-h-screen p-8' : 'px-4'}`}>
      {/* Title and subtitle overlay for grid */}
      {(title || subtitle) && (
        <div className={`mb-8 ${isFullscreen ? 'text-center' : ''}`}>
          {title && (
            <h2
              className={`image-gallery-title font-bold mb-3 ${isFullscreen ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}
              style={{
                fontSize: isFullscreen ? 'clamp(2rem, 7vw, 3rem)' : 'clamp(1.5rem, 5vw, 2.5rem)',
                lineHeight: '1.1',
                textShadow: isFullscreen ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
              }}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p
              className={`image-gallery-subtitle mb-6 ${isFullscreen ? 'text-white' : 'text-gray-600 dark:text-gray-300'}`}
              style={{
                fontSize: isFullscreen ? 'clamp(1rem, 4vw, 1.5rem)' : 'clamp(1rem, 3vw, 1.25rem)',
                textShadow: isFullscreen ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
              }}
            >
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Modern masonry-style grid layout */}
      <div className="image-gallery-items columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-3 md:gap-4 space-y-3 md:space-y-4">
        {images.map((img, idx) => (
          <div
            key={idx}
            className="relative break-inside-avoid mb-3 md:mb-4 overflow-hidden rounded-xl cursor-pointer group shadow-md hover:shadow-xl transition-all duration-300"
            onClick={() => openLightbox(idx)}
          >
            <Image
              src={img.src}
              alt={img.alt || `Image ${idx + 1}`}
              width={400}
              height={300}
              className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
              style={{
                objectFit: 'cover',
                aspectRatio: '1/1'
              }}
              sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, (max-width: 1600px) 25vw, 20vw"
              placeholder={img.blurDataURL ? 'blur' : undefined}
              blurDataURL={img.blurDataURL || undefined}
            />
            
            {/* Modern hover overlay with subtle gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4">
              <div className="text-white text-center">
                <div className="w-12 h-12 mx-auto mb-2 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                </div>
                <span className="text-sm font-medium">View</span>
              </div>
            </div>

            {/* Subtle border for definition */}
            <div className="absolute inset-0 rounded-xl ring-1 ring-black/10 dark:ring-white/10 pointer-events-none"></div>
          </div>
        ))}
      </div>

      {/* CTA button for grid */}
      {ctaLink && ctaLabel && (
        <div className={`mt-8 ${isFullscreen ? 'text-center' : ''}`}>
          <a
            href={ctaLink}
            className={`inline-flex items-center gap-2 rounded-full border-2 font-bold px-8 py-3 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ${
              isFullscreen ? 'text-lg' : ''
            }`}
            style={{
              backgroundColor: `var(--nav-${colorForLink(ctaLink)}-bg)`,
              color: `var(--nav-${colorForLink(ctaLink)}-text)`,
              borderColor: `var(--nav-${colorForLink(ctaLink)}-text)`,
            }}
            rel="noopener noreferrer"
          >
            {ctaLabel}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
            </svg>
          </a>
        </div>
      )}
    </div>
  );

  // Slider variant component (existing implementation)
  const SliderGallery = () => (
    <div
      className={`relative overflow-hidden ${
        isFullscreen
          ? (isRelaunch ? RELAUNCH_HERO_HEIGHT_CLASS : 'h-[var(--hero-height,100svh)] md:h-[var(--hero-height,100dvh)]')
          : 'w-full h-[300px] md:h-[400px] rounded-lg'
      }`}
      style={
        isFullscreen
          ? { width: '100vw', marginLeft: 'calc(50% - 50vw)', background: images[0]?.color?.bgColor }
          : { maxWidth: '100%' }
      }
    >
      <div
        className="w-fit h-full max-w-full"
        style={{ 
          width: '100%', 
          height: '100%'
        }}
      >
        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          navigation
          pagination={{ clickable: true }}
          spaceBetween={0}
          slidesPerView={1}
          style={{
            width: '100%',
            height: '100%'
          }}
          autoplay={autocycle ? { delay: autocycle * 1000, disableOnInteraction: false } : false}
        >
          {images.map((img, idx) => (
            <SwiperSlide
              key={idx}
              className="relative w-full h-full cursor-pointer"
              onClick={() => openLightbox(idx)}
            >
              <Image
                src={img?.src}
                alt={img?.alt || `Image ${idx + 1}`}
                fill
                // .ds-image only for non-hero usage - the fullscreen homepage
                // hero should never get a margin/rounding/shadow treatment.
                className={isFullscreen ? undefined : 'ds-image'}
                style={{ objectFit: 'cover' }}
                sizes={isFullscreen ? "100vw" : "(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"}
                priority={idx === 0}
                placeholder={img?.blurDataURL ? 'blur' : undefined}
                blurDataURL={img?.blurDataURL || undefined}
              />

              {/* Title/subtitle overlay, rendered per-slide (not from client
                  state) so its contrast color is baked in at server-render
                  time from THIS slide's own precomputed color - no runtime
                  "which slide is active" tracking that could ever glitch or
                  briefly fall back to a hardcoded default. */}
              {(title || subtitle) && (
                <div
                  className={`absolute z-10 drop-shadow-lg ${isFullscreen ? 'left-8 top-8' : 'left-4 top-4'}`}
                  style={{
                    left: '2rem',
                    top: '2rem',
                    width: 'calc(100% - 4rem)',
                    maxWidth: '100%',
                    color: img?.color?.textColor || '#ffffff',
                  }}
                >
                  {title && (
                    <h2
                      className="h1 font-bold mb-2"
                      style={{
                        fontSize: isFullscreen ? 'clamp(2rem, 7vw, 3rem)' : 'clamp(1.25rem, 4vw, 2rem)',
                        lineHeight: '1.1',
                        color: img?.color?.textColor || '#ffffff',
                        textShadow: img?.color?.textShadow || '0 2px 8px rgba(0,0,0,0.45)',
                      }}
                    >
                      {title}
                    </h2>
                  )}
                  {subtitle && (
                    <p
                      className="h2 mb-2"
                      style={{
                        fontSize: isFullscreen ? 'clamp(1rem, 4vw, 1.5rem)' : 'clamp(0.875rem, 3vw, 1.125rem)',
                        maxWidth: isFullscreen ? '80vw' : '90%',
                        color: img?.color?.textColor || '#ffffff',
                        textShadow: img?.color?.textShadow || '0 2px 8px rgba(0,0,0,0.45)',
                      }}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* CTA button for slider */}
      {ctaLink && ctaLabel && (
        <a
          href={ctaLink}
          className={`absolute z-10 shadow-lg ${
            isFullscreen ? 'left-8 bottom-8' : 'left-4 bottom-4'
          } ${
            isRelaunch
              ? RELAUNCH_CTA_CLASS
              : 'font-bold px-6 py-2.5 transition-all duration-200 rounded-full border-2 hover:scale-105'
          }`}
          style={{
            left: '2rem',
            bottom: '2rem',
            fontSize: isFullscreen ? 'clamp(1rem, 4vw, 1.25rem)' : '1rem',
            ...(isRelaunch ? {} : {
              backgroundColor: `var(--nav-${colorForLink(ctaLink)}-bg)`,
              color: `var(--nav-${colorForLink(ctaLink)}-text)`,
              borderColor: `var(--nav-${colorForLink(ctaLink)}-text)`,
            }),
          }}
          rel="noopener noreferrer"
        >
          {ctaLabel}
        </a>
      )}
    </div>
  );

  return (
    <>
      {variant === 'grid' ? <GridGallery /> : <SliderGallery />}
      
      {/* Lightbox */}
      <Lightbox
        images={images}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        onNext={nextImage}
        onPrev={prevImage}
      />
    </>
  );
};

export default ImageGallery;
